import type { RepositoryIndexingMode } from "@arti/shared-source-control/indexing/runMode";
import type { FC } from "react";

import { Button } from "../common/Button";
import { IndexingModeSelector } from "./IndexingModeSelector";

interface IndexingRunSelectionControlsProps {
  bulkMode: RepositoryIndexingMode;
  hasLatestRun: boolean;
  repositoryCount: number;
  selectedCount: number;
  selectingFailed: boolean;
  selectedSlugCount: number;
  submitting: boolean;
  onApplyBulkMode: () => void;
  onBulkModeChange: (mode: RepositoryIndexingMode) => void;
  onClearAll: () => void;
  onSelectAll: () => void;
  onSelectFailedFromLastRun: () => void;
}

export const IndexingRunSelectionControls: FC<
  IndexingRunSelectionControlsProps
> = ({
  bulkMode,
  hasLatestRun,
  repositoryCount,
  selectedCount,
  selectingFailed,
  selectedSlugCount,
  submitting,
  onApplyBulkMode,
  onBulkModeChange,
  onClearAll,
  onSelectAll,
  onSelectFailedFromLastRun,
}) => (
  <>
    <div className="flex flex-wrap items-center justify-between gap-3 border border-slate-300 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-arti-panel">
      <div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
          Run Scope
        </div>
        <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">
          {selectedCount} of {repositoryCount} repos selected
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSelectAll}
          disabled={submitting || repositoryCount === 0}
        >
          Select All
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          disabled={submitting || repositoryCount === 0}
        >
          Clear All
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onSelectFailedFromLastRun}
          disabled={
            submitting ||
            selectingFailed ||
            repositoryCount === 0 ||
            !hasLatestRun
          }
          loading={selectingFailed}
          loadingText="Loading..."
        >
          Select Failed from Last Run
        </Button>
      </div>
    </div>

    <div className="grid gap-3 border border-slate-300 px-3 py-3 dark:border-slate-700 md:grid-cols-[1fr,auto] md:items-center">
      <div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
          Bulk Mode
        </div>
        <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">
          Apply one mode across the currently selected repositories.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <IndexingModeSelector
          disabled={submitting || selectedSlugCount === 0}
          mode={bulkMode}
          onChange={onBulkModeChange}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={onApplyBulkMode}
          disabled={submitting || selectedSlugCount === 0}
        >
          Apply to Selected
        </Button>
      </div>
    </div>
  </>
);
